/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useRef } from "react";
import { useAppDispatch } from "hooks";
import {
	useGetRewardPWAQuery,
	useGetRewardStatusPWAQuery,
	useLaunchPWAMutation
} from "redux/apis/pwa";
import {
	PWARewardLaunchResponse,
	PWARewardResponse,
	PWARewardStatusResponse
} from "interfaces/pwa";
import { useWebStoreContext } from "contexts/WebStoreProvider";
import { useEnvContext } from "contexts/EnvProvider";
import { FeaturesFlags } from "helpers";
import { useAppSelector } from "hooks";
import { selectPWAState, setHasCompletedPWAInit } from "redux/slices/pwa";
import { usePopUpsV2 } from "components/PopUpManagerV2/hooks/usePopUpsV2";
import { getIsStandalonePWA } from "../helpers/getIsStandalonePWA";

/**
 * Triggers the PWA reward popup when the app is running
 * in standalone (installed) mode, on Android, iOS or desktop.
 *
 * Detection:
 * - `display-mode: standalone` or `window-controls-overlay` (Chrome/Edge, etc.).
 * - `navigator.standalone === true` (iOS Safari when opened from home screen).
 */
export const usePWATriggerReward = (): void => {
	const { authToken } = useWebStoreContext();
	const { isFeatureFlagEnabled } = useEnvContext();
	const isPWAEnabled = isFeatureFlagEnabled(FeaturesFlags.PWA);
	const hasTriggeredRef = useRef(false);
	const dispatch = useAppDispatch();

	const { open } = usePopUpsV2();
	const { data: pwaReward }: { data?: PWARewardResponse } =
		useGetRewardPWAQuery("", {
			skip: !authToken || !isPWAEnabled,
			refetchOnFocus: true
		});
	const { data: pwaRewardStatus }: { data?: PWARewardStatusResponse } =
		useGetRewardStatusPWAQuery("", {
			skip: !authToken || !isPWAEnabled,
			refetchOnFocus: true
		});
	const [launchPWA] = useLaunchPWAMutation();

	const loadPWA = async () => {
		// eslint-disable-next-line no-console
		// console.log("[PWA Reward] loadPWA called");
		await launchPWA({})
			.unwrap()
			.then((data: PWARewardLaunchResponse) => {
				if (data?.pwaReward && !pwaRewardStatus?.pwaReward?.rewardId) {
					// dispatch(onOpenPopUp("pwa-reward-popup"));
					open({
						variant: "pwa-reward-popup",
						id: "pwa-reward-popup",
						replaceIfExists: true
					});
				}
			});

		// eslint-disable-next-line no-console
		// console.log("[PWA Reward] Marking hasCompletedPWAInit = true");
		dispatch(setHasCompletedPWAInit(true));
	};

	useEffect(() => {
		if (!authToken) {
			hasTriggeredRef.current = false;
			return;
		}

		if (!isPWAEnabled || hasTriggeredRef.current) {
			return;
		}

		if (typeof window === "undefined") {
			return;
		}

		const isStandalone = getIsStandalonePWA();
		// eslint-disable-next-line no-console
		// console.log("[PWA Reward] effect state", {
		// 	authToken: !!authToken,
		// 	isPWAEnabled,
		// 	isStandalone,
		// 	hasTriggered: hasTriggeredRef.current,
		// 	pwaRewardLoaded: !!pwaReward,
		// 	pwaRewardStatusLoaded: !!pwaRewardStatus
		// });
		if (isStandalone && pwaReward && pwaRewardStatus) {
			hasTriggeredRef.current = true;
			loadPWA();
		}
	}, [dispatch, authToken, isPWAEnabled, pwaReward, pwaRewardStatus]);
};
